// When a chat turn or section generation has been
// running for more than a few seconds without producing any visible
// progress, the user reads the typing dots or the rotating phrases
// as "is it stuck?". Show an explicit duration and a one-line
// "still working" hint so the user knows the system is alive.

import React, { useEffect, useState } from "react";

interface Props {
  /** Whether the long-running operation is currently active. */
  active: boolean;
  /** Hide the hint until this many ms have elapsed. Default 8 s. */
  delayMs?: number;
  /** How often to refresh the elapsed-second display. Default 1 s. */
  refreshMs?: number;
}

function formatElapsed(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

export const LongRunningIndicator: React.FC<Props> = ({
  active,
  delayMs = 8000,
  refreshMs = 1000,
}) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!active) {
      setElapsed(0);
      return undefined;
    }
    const started = Date.now();
    const id = window.setInterval(() => {
      setElapsed(Date.now() - started);
    }, refreshMs);
    return () => window.clearInterval(id);
  }, [active, refreshMs]);
  if (!active || elapsed < delayMs) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="long-running-indicator"
      data-elapsed-ms={elapsed}
      className="llm-chat-long-running-indicator"
      style={{
        marginTop: 6,
        fontSize: 11,
        color: "#6b7280",
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span aria-hidden="true">⏳</span>
      <span>Still working — {formatElapsed(elapsed)} elapsed.</span>
    </div>
  );
};

export default LongRunningIndicator;
